import type { JSX } from 'react';
import { Suspense, lazy, useEffect } from 'react';
import { useMasterPeekOpen, closeMasterPeek, openMasterPeek } from '../lib/master-peek';
import { useIsMobile } from '../hooks/useIsMobile';
import { useRouteHref } from '../lib/router';

// Code-split: o dock arrasta o MasterChatPage (xterm). So carrega quando o peek abre.
const MasterPeek = lazy(() => import('./MasterPeek').then((m) => ({ default: m.MasterPeek })));

/**
 * FASE 121 (121.B.2/121.B.4) — host do peek do master + atalho mobile, montado na RAIZ do roteador (overlay
 * sobre qualquer pagina). DESKTOP: renderiza o dock lateral quando aberto (nunca sobre a propria pagina cheia
 * do master, p/ nao ter 2 sessoes de xterm do master). MOBILE: sem split (tela pequena) — o peek e forcado a
 * fechar. AL.E3 (AUD.17, 20/09/2026): o FAB "master" que flutuava sobre o conteudo do deck SAIU — o atalho
 * vive na folha "Acoes" do deck (Destinos · MASTER operacional), ao lado do Produto; nada mais flutua sobre o
 * launcher alem da barra de navegacao inferior.
 */
export function MasterPeekHost(): JSX.Element | null {
  const open = useMasterPeekOpen();
  const isMobile = useIsMobile();
  const href = useRouteHref();
  const url = typeof window !== 'undefined' ? new URL(href, window.location.origin) : null;
  const onMasterFull =
    url != null &&
    ((url.pathname.replace(/\/+$/, '') === '/chat' && url.searchParams.get('master') === '1') ||
      (url.pathname.replace(/\/+$/, '') === '/central' && url.searchParams.get('tab') === 'master'));

  // Mobile nunca usa o dock (tela pequena inviabiliza o split): garante fechado.
  useEffect(() => {
    if (isMobile && open) closeMasterPeek();
  }, [isMobile, open]);

  // AL.E3 — no celular nao ha dock nem FAB: o atalho ao MASTER esta na folha "Acoes" do deck.
  if (isMobile) return null;

  if (!open || onMasterFull || url?.pathname === '/chat' || url?.pathname === '/') return null;

  return (
    <>
      <div className="master-peek-backdrop" onClick={closeMasterPeek} data-testid="master-peek-backdrop" />
      <Suspense fallback={<aside className="master-peek master-peek--loading">carregando master…</aside>}>
        <MasterPeek />
      </Suspense>
    </>
  );
}

/** Reexport de conveniencia p/ os pontos de entrada (MasterControl) abrirem o dock. */
export { openMasterPeek };
